import { findPayload } from '@execonline-inc/collections';
import { when } from '@execonline-inc/maybe-adapter';
import { assertNever } from '@kofno/piper';
import { Maybe, fromEmpty, just, nothing } from 'maybeasy';
import { action, computed, makeObservable, observable } from 'mobx';
import {
  FormTemplate,
  findField,
  stringFieldOutValue,
} from '../../../Resource/FormTemplates/Types';
import { Link } from '../../../Resource/Types';
import {
  LearningDesignResource,
  ProductCollectionResource,
  ProductLicenseResource,
} from '../../LearningCollections/Types';
import { WizardStepItem } from '../../WizardStepGroup/Types';
import LearningDesignExperienceStore from './Experience/Store';
import PublishingStore from './Edit/CurrentCollectionStep/Share/PublishSection/Store';
import LearningDesignsStore from './LearningDesigns/Store';
import ProductLicensesStore from './ProductLicenses/Store';
import {
  LearningCollectionError,
  LearningCollectionNewFormResource,
  LearningCollectionState,
  ReadyArgs,
  building,
  choosingProductLicense,
  creating,
  loadError,
  loading,
  missingTemplates,
  newFormSubmissionError,
  ready,
  reloadAllExperiences,
  waiting,
} from './Types';
import { FileMetaData } from './Edit/CurrentCollectionStep/Details/S3ImageUpload/Types';

class LearningCollectionStore {
  @observable
  state: LearningCollectionState = waiting();

  readonly learningDesignsStore = new LearningDesignsStore();
  readonly publishingStore = new PublishingStore();
  readonly productLicensesStore = new ProductLicensesStore();

  constructor() {
    makeObservable(this);
  }

  @action
  loading = () => {
    switch (this.state.kind) {
      case 'loading':
      case 'ready':
      case 'building':
      case 'creating':
      case 'load-error':
      case 'new-form-submission-error':
      case 'choosing-product-license':
      case 'reload-all-experiences':
        break;
      case 'waiting':
        this.state = loading();
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  ready = (args: ReadyArgs) => {
    switch (this.state.kind) {
      case 'waiting':
      case 'ready':
      case 'building':
      case 'creating':
      case 'load-error':
      case 'new-form-submission-error':
      case 'choosing-product-license':
      case 'reload-all-experiences':
        break;
      case 'loading':
        this.state = ready(args.resource, args.template);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  choosingProductLicense = () => {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'creating':
      case 'load-error':
      case 'new-form-submission-error':
      case 'choosing-product-license':
      case 'reload-all-experiences':
        break;
      case 'ready':
      case 'building':
        this.state = choosingProductLicense(this.state);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  building = (learningCollectionNewFormResource: LearningCollectionNewFormResource) => {
    const state = this.state;
    switch (state.kind) {
      case 'waiting':
      case 'loading':
      case 'ready':
      case 'building':
      case 'creating':
      case 'load-error':
      case 'new-form-submission-error':
      case 'choosing-product-license':
        break;
      case 'reload-all-experiences':
        this.state = building(state, learningCollectionNewFormResource);
        break;
      default:
        assertNever(state);
    }
  };

  @action
  saving = (fileMetaData: Maybe<FileMetaData>) => {
    const state = this.state;
    switch (state.kind) {
      case 'waiting':
      case 'loading':
      case 'ready':
      case 'creating':
      case 'load-error':
      case 'choosing-product-license':
      case 'reload-all-experiences':
        break;
      case 'building':
        this.state = creating(state.learningCollectionFormResource, state, fileMetaData);
        break;
      case 'new-form-submission-error':
        this.state = creating(state.learningCollectionFormResource, state, nothing());
        break;
      default:
        assertNever(state);
    }
  };

  @action
  loadError = (error: LearningCollectionError) => {
    switch (this.state.kind) {
      case 'waiting':
      case 'ready':
      case 'building':
      case 'creating':
      case 'load-error':
      case 'new-form-submission-error':
      case 'choosing-product-license':
      case 'reload-all-experiences':
        break;
      case 'loading':
        this.state = loadError(error);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  reloadAllExperiences = () => {
    switch (this.state.kind) {
      case 'waiting':
      case 'ready':
      case 'building':
      case 'creating':
      case 'load-error':
      case 'new-form-submission-error':
      case 'loading':
      case 'reload-all-experiences':
        break;
      case 'choosing-product-license':
        this.state = reloadAllExperiences(this.state);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  templateLoadError = () => {
    switch (this.state.kind) {
      case 'waiting':
      case 'ready':
      case 'building':
      case 'load-error':
      case 'new-form-submission-error':
      case 'choosing-product-license':
      case 'reload-all-experiences':
        break;
      case 'loading':
      case 'creating':
        this.state = loadError(missingTemplates);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  newFormSubmissionError = (
    learningCollectionFormResource: LearningCollectionNewFormResource,
    error: LearningCollectionError,
    template: FormTemplate,
  ) => {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'ready':
      case 'building':
      case 'load-error':
      case 'new-form-submission-error':
      case 'choosing-product-license':
      case 'reload-all-experiences':
        break;
      case 'creating':
        this.state = newFormSubmissionError(
          learningCollectionFormResource,
          template,
          error,
          this.state,
        );
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  selectProductLicense = (productLicenseId: number) => {
    const state = this.state;
    switch (state.kind) {
      case 'waiting':
      case 'loading':
      case 'ready':
      case 'building':
      case 'creating':
      case 'load-error':
      case 'new-form-submission-error':
      case 'reload-all-experiences':
        break;
      case 'choosing-product-license':
        this.productLicensesStore.productLicensesResource.do((productLicensesResource) => {
          findPayload<ProductLicenseResource>(
            productLicenseId,
            productLicensesResource.payload.activeProductLicenses,
          ).do((productLicense) => {
            state.selectedProductLicense = just(productLicense);
          });
        });
        break;
      default:
        assertNever(state);
    }
  };

  @computed
  get selectedProductLicense(): Maybe<ProductLicenseResource> {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'ready':
      case 'load-error':
        return nothing();
      case 'reload-all-experiences':
      case 'choosing-product-license':
      case 'building':
      case 'creating':
      case 'new-form-submission-error':
        return this.state.selectedProductLicense;
    }
  }

  @computed
  get activeSelectedProductLicense(): Maybe<ProductLicenseResource> {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'ready':
      case 'load-error':
      case 'building':
      case 'creating':
      case 'new-form-submission-error':
      case 'reload-all-experiences':
        return nothing();
      case 'choosing-product-license':
        return just({})
          .assign('productLicensesResource', this.productLicensesStore.productLicensesResource)
          .assign('selectedProductLicense', this.state.selectedProductLicense)
          .andThen(({ productLicensesResource, selectedProductLicense }) => {
            const productLicenses = productLicensesResource.payload.activeProductLicenses.filter(
              (item) => item.payload.id === selectedProductLicense.payload.id,
            );
            return when(productLicenses.length === 1, selectedProductLicense);
          });
    }
  }

  @computed
  get selectedProgramIds(): number[] {
    switch (this.state.kind) {
      case 'waiting':
      case 'ready':
      case 'creating':
      case 'load-error':
      case 'new-form-submission-error':
      case 'choosing-product-license':
      case 'reload-all-experiences':
      case 'loading':
      case 'building':
        return [];
    }
  }

  @computed
  get template(): Maybe<FormTemplate> {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'load-error':
        return nothing();
      case 'reload-all-experiences':
      case 'choosing-product-license':
      case 'ready':
      case 'building':
      case 'creating':
      case 'new-form-submission-error':
        return just(this.state.template);
    }
  }

  learningDesignExperienceStores = (
    _learningDesign: LearningDesignResource | ProductCollectionResource,
  ): ReadonlyArray<LearningDesignExperienceStore> => {
    return [];
  };

  @computed
  get onDemandExperienceStores(): ReadonlyArray<LearningDesignExperienceStore> {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'ready':
      case 'load-error':
      case 'choosing-product-license':
      case 'reload-all-experiences':
        return [];
      case 'building':
      case 'creating':
      case 'new-form-submission-error':
        return this.state.browseAllExperiences.experienceStores.filter(
          ({ experienceResource }) => experienceResource.payload.programInstanceOptions.onDemand,
        );
    }
  }

  @computed
  get canSave(): boolean {
    const state = this.state;
    switch (state.kind) {
      case 'waiting':
      case 'loading':
      case 'ready':
      case 'creating':
      case 'load-error':
      case 'choosing-product-license':
      case 'reload-all-experiences':
        return false;
      case 'building':
      case 'new-form-submission-error':
        return fromEmpty(
          findField('name', 'string', state.template.fields)
            .map(stringFieldOutValue)
            .getOrElseValue(''),
        ).cata({
          Nothing: () => false,
          Just: () => true,
        });
    }
  }

  @computed
  get wizardItems(): Maybe<WizardStepItem[]> {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'load-error':
        return nothing();
      case 'ready':
      case 'choosing-product-license':
      case 'reload-all-experiences':
        return just([
          { label: 'Start', step: 1, state: 'current' },
          { label: 'Describe', step: 2, state: 'unstarted' },
          { label: 'Customize', step: 3, state: 'unstarted' },
          { label: 'Preview', step: 4, state: 'unstarted' },
          { label: 'Share', step: 5, state: 'unstarted' },
        ]);
      case 'creating':
      case 'building':
        return just([
          { label: 'Start', step: 1, state: 'completed' },
          { label: 'Describe', step: 2, state: 'current' },
          { label: 'Customize', step: 3, state: 'unstarted' },
          { label: 'Preview', step: 4, state: 'unstarted' },
          { label: 'Share', step: 5, state: 'unstarted' },
        ]);
      case 'new-form-submission-error':
        return nothing();
    }
  }

  @computed
  get browseAll(): boolean {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'load-error':
        return false;
      case 'building':
      case 'ready':
      case 'creating':
      case 'new-form-submission-error':
      case 'choosing-product-license':
      case 'reload-all-experiences':
        return findField('browse_all', 'boolean', this.state.template.fields)
          .map((field) => field.value)
          .getOrElseValue(false);
    }
  }

  @computed
  get browseAllCustomized(): boolean {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'load-error':
      case 'choosing-product-license':
      case 'reload-all-experiences':
      case 'ready':
        return false;
      case 'building':
      case 'creating':
      case 'new-form-submission-error':
        this.state.browseAllExperiences;
        return (
          JSON.stringify(
            this.state.learningCollectionFormResource.payload.allExperiences.map(
              (experience) => experience.payload.id,
            ),
          ) !==
          JSON.stringify(
            this.state.browseAllExperiences.experienceStores.map(
              ({ experienceResource }) => experienceResource.payload.id,
            ),
          )
        );
    }
  }

  @computed
  get availableExtensionIds(): number[] {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'load-error':
      case 'choosing-product-license':
      case 'reload-all-experiences':
        return [];
      case 'ready':
      case 'building':
      case 'creating':
      case 'new-form-submission-error':
        return this.state.learningCollectionFormResource.payload.availableExtensionIds;
    }
  }

  @computed
  get links(): ReadonlyArray<Link> {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'load-error':
        return [];
      case 'reload-all-experiences':
      case 'ready':
      case 'building':
      case 'creating':
      case 'new-form-submission-error':
      case 'choosing-product-license':
        return this.state.learningCollectionFormResource.links;
    }
  }
}

export default LearningCollectionStore;
